const storageKey = 'sparkone.voice.browser-tab.v2';
const activationStorageKey = 'sparkone.voice.browser-activation.v1';
const tabWindowNamePrefix = 'sparkone-voice-tab:';

const uuidPattern =
  /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function createUuid(): string {
  if (typeof crypto.randomUUID === 'function') return crypto.randomUUID();
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const value = Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
  return `${value.slice(0, 8)}-${value.slice(8, 12)}-${value.slice(12, 16)}-${value.slice(16, 20)}-${value.slice(20)}`;
}

let pageTabId: string | null = null;

/**
 * In-memory fallback used when localStorage is unavailable (SSR, privacy mode,
 * or disabled storage). The identity is stable for the lifetime of this module
 * instance so a single server render or restricted session still gets one UUID
 * rather than churning a fresh value on every call.
 */
const fallbackBrowserDeviceStorage: Storage = (() => {
  const store = new Map<string, string>();
  return {
    get length(): number {
      return store.size;
    },
    clear(): void {
      store.clear();
    },
    getItem(key: string): string | null {
      return store.has(key) ? (store.get(key) as string) : null;
    },
    key(index: number): string | null {
      return Array.from(store.keys())[index] ?? null;
    },
    removeItem(key: string): void {
      store.delete(key);
    },
    setItem(key: string, value: string): void {
      store.set(key, String(value));
    },
  };
})();

/**
 * Per-profile identity store. The device-instance id is intentionally
 * profile-scoped (localStorage) so the server sees one stable installation per
 * browser profile instead of a fresh UUID, and churned installation row, on
 * every tab open or refresh. sessionStorage/privacy-mode/SSR fall back to the
 * in-memory shim above.
 */
function resolveDefaultBrowserDeviceStorage(): Storage {
  if (typeof window === 'undefined') return fallbackBrowserDeviceStorage;
  try {
    if (window.localStorage) return window.localStorage;
  } catch {
    // Privacy mode throws on storage access; fall through to the in-memory shim.
  }
  return fallbackBrowserDeviceStorage;
}

/**
 * Stable per-profile identity. localStorage survives tab close, refresh, and
 * independent tab opens so each browser profile maps to one installation row.
 * The activation reclaim token and tab fence below remain sessionStorage /
 * window.name based so per-tab fences still hold under this stable identity.
 */
function readOrCreateBrowserDeviceInstanceId(storage: Storage): string {
  const existing = storage.getItem(storageKey);
  if (existing && uuidPattern.test(existing)) return existing;
  const created = createUuid();
  storage.setItem(storageKey, created);
  return created;
}

export function getBrowserDeviceInstanceId(
  storage: Storage = resolveDefaultBrowserDeviceStorage(),
): string {
  try {
    return readOrCreateBrowserDeviceInstanceId(storage);
  } catch {
    // Some privacy-restricted contexts expose localStorage but throw from
    // getItem/setItem. Treat those the same as unavailable storage so one page
    // lifetime still has a stable identity instead of failing or churning IDs.
    return readOrCreateBrowserDeviceInstanceId(fallbackBrowserDeviceStorage);
  }
}

export function replaceRetiredBrowserDeviceInstanceId(
  retiredId: string,
  storage: Storage = resolveDefaultBrowserDeviceStorage(),
): string {
  const replace = (target: Storage) => {
    const current = target.getItem(storageKey);
    if (current && uuidPattern.test(current) && current !== retiredId) {
      return current;
    }
    const replacement = createUuid();
    target.setItem(storageKey, replacement);
    return replacement;
  };

  try {
    return replace(storage);
  } catch {
    return replace(fallbackBrowserDeviceStorage);
  }
}

/**
 * A reclaim token stored with the tab identity. It survives a normal reload so
 * the server can reconcile the exact installation without creating a successor.
 * It is an opaque routing fence, never a SIP credential.
 */
export function getBrowserActivationId(
  storage: Storage = window.sessionStorage,
): string {
  const existing = storage.getItem(activationStorageKey);
  if (existing && uuidPattern.test(existing)) return existing;
  const created = createUuid();
  storage.setItem(activationStorageKey, created);
  return created;
}

/**
 * Page/tab-instance fence. window.name survives a same-tab reload but is not
 * persisted in storage, so a copied sessionStorage tab cannot reclaim the
 * original tab's active lease merely by copying its reclaim token.
 */
export function getBrowserTabId(): string {
  if (pageTabId) return pageTabId;
  const current = typeof window.name === 'string' ? window.name : '';
  if (current.startsWith(tabWindowNamePrefix)) {
    const candidate = current.slice(tabWindowNamePrefix.length);
    if (uuidPattern.test(candidate)) return (pageTabId = candidate);
  }
  const created = createUuid();
  window.name = `${tabWindowNamePrefix}${created}`;
  pageTabId = created;
  return created;
}

export function resetBrowserActivationIdForTesting(): void {
  pageTabId = null;
}

export const browserActivationStorageKey = activationStorageKey;

export const browserDeviceInstanceStorageKey = storageKey;
export const isBrowserDeviceInstanceId = (value: string): boolean => uuidPattern.test(value);
export const isBrowserActivationId = isBrowserDeviceInstanceId;
